import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

// Read Vite env variables. Use a single TEMPLATE_ID env var; fall back to contact/admission specific vars if present.
const EMAILJS_SERVICE_ID = String(import.meta.env.VITE_EMAILJS_SERVICE_ID || '');
const EMAILJS_TEMPLATE_ID = String(import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '') || String(import.meta.env.VITE_EMAILJS_TEMPLATE_CONTACT_ID || '') || String(import.meta.env.VITE_EMAILJS_TEMPLATE_ADMISSION_ID || '');
const EMAILJS_PUBLIC_KEY = String(import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '');

type FormType = 'contact' | 'admission';

// SharedForm: builds a single `message` string containing all fields (ordered & labeled)
// and sends via EmailJS using the template param `message` so your EmailJS "Contact Us" template
// receives the entire body exactly as built here.
const SharedForm: React.FC<{ formType?: FormType }> = ({ formType = 'contact' }) => {
  // Centralized state for both common and admission-specific fields
  const [formData, setFormData] = useState({
    // common
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    // admission-specific
    studentName: '',
    dateOfBirth: '',
    gender: '',
    form: '',
    studentType: 'day',
    parentName: '',
    parentPhone: '',
    previousSchool: '',
    address: '',
    medicalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Simple controlled input handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Build a neatly ordered plain-text message including all fields.
  // We build the message here in code so the exact ordering/labels are under our control.
  const buildMessageText = (type: FormType) => {
    const lines: string[] = [];
    lines.push(`Form Type: ${type === 'admission' ? 'Admissions' : 'Contact'}`);
    lines.push(`Submitted At: ${new Date().toLocaleString()}`);
    lines.push('');

    // Common fields (Full Name, Email, Phone, Subject, Message)
    lines.push('--- Contact Information ---');
    lines.push(`Full Name: ${formData.fullName || formData.studentName || 'N/A'}`);
    lines.push(`Email: ${formData.email || 'N/A'}`);
    lines.push(`Phone: ${formData.phone || 'N/A'}`);
    if (formData.subject) lines.push(`Subject: ${formData.subject}`);
    if (formData.message) {
      lines.push('Message:');
      // Preserve newlines in the user's message
      lines.push(formData.message || 'N/A');
    }

    // If this is an admissions submission, include the admission-specific block in order.
    if (type === 'admission' || formData.studentName || formData.dateOfBirth || formData.parentName) {
      lines.push('');
      lines.push('--- Admissions Details ---');
      lines.push(`Student Name: ${formData.studentName || 'N/A'}`);
      lines.push(`Date of Birth: ${formData.dateOfBirth || 'N/A'}`);
      lines.push(`Gender: ${formData.gender || 'N/A'}`);
  lines.push(`Form: ${formData.form || 'N/A'}`);
      lines.push(`Student Type: ${formData.studentType || 'N/A'}`);
      lines.push(`Parent / Guardian: ${formData.parentName || 'N/A'}`);
      lines.push(`Parent Phone: ${formData.parentPhone || 'N/A'}`);
      lines.push(`Previous School: ${formData.previousSchool || 'N/A'}`);
      lines.push('Home Address:');
      lines.push(formData.address || 'N/A');
      lines.push('Medical Information:');
      lines.push(formData.medicalInfo || 'N/A');
    }

    lines.push('');
    lines.push('--- End of message ---');
    return lines.join('\n');
  };

  // Submit handler: build message and send as the `message` parameter to EmailJS
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation: require at least name and either message or (for admissions) student name
    if (!formData.fullName && !formData.studentName) {
      setStatus('error');
      setErrorMsg('Please provide a name.');
      return;
    }

    if (formType === 'contact' && !formData.message) {
      setStatus('error');
      setErrorMsg('Please provide a message.');
      return;
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setStatus('error');
      setErrorMsg('EmailJS is not configured. Please set env variables.');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setErrorMsg(null);

    try {
      const messageText = buildMessageText(formType);

      // EmailJS template parameters. We pass the full message as the 'message' field
      // since your "Contact Us" template expects `message`.
      const templateParams = {
        to_email: 'info@elitecollege.co.zw',
        subject: formType === 'admission' ? 'New Admissions Application' : (formData.subject || 'New Contact Message'),
        message: messageText // <-- entire formatted message sent as `message`
      } as Record<string, string>;

      // Send the email via EmailJS client SDK
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

      setStatus('success');

      // Clear fields after a short delay so user can see confirmation
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          studentName: '',
          dateOfBirth: '',
          gender: '',
          form: '',
          studentType: 'day',
          parentName: '',
          parentPhone: '',
          previousSchool: '',
          address: '',
          medicalInfo: ''
        });
      }, 800);
    } catch (err: any) {
      console.error('Email send error', err);
      setStatus('error');
      setErrorMsg(err?.text || err?.message || 'Unknown error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Hidden field: form type (programmatic). Included in the message header too. */}
      <input type="hidden" name="formType" value={formType} />

      {/* Common fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
        <input name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="Your full name" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="you@example.com" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
        <input name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="Phone number" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
        <input name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="Subject (optional)" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
        <textarea name="message" value={formData.message} onChange={handleChange} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical" placeholder="Write your message..."></textarea>
      </div>

      {/* Admission-specific fields, shown when formType is 'admission' */}
      {formType === 'admission' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Student Name</label>
            <input name="studentName" value={formData.studentName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="Student full name" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
              <input name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <select name="gender" value={formData.gender} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm">
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Form</label>
              <input name="form" value={formData.form} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Form 1" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Student Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center"><input className="mr-2" type="radio" name="studentType" value="day" checked={formData.studentType === 'day'} onChange={handleChange as any} />Day</label>
              <label className="flex items-center"><input className="mr-2" type="radio" name="studentType" value="boarding" checked={formData.studentType === 'boarding'} onChange={handleChange as any} />Boarding</label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent / Guardian Name</label>
              <input name="parentName" value={formData.parentName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent Phone</label>
              <input name="parentPhone" value={formData.parentPhone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Previous School</label>
            <input name="previousSchool" value={formData.previousSchool} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Home Address</label>
            <textarea name="address" value={formData.address} onChange={handleChange} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Medical Information</label>
            <textarea name="medicalInfo" value={formData.medicalInfo} onChange={handleChange} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm resize-vertical" placeholder="Allergies, medication, conditions..." />
          </div>
        </>
      )}

      {/* Status */}
      {status === 'success' && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800">Message sent successfully. We'll contact you soon.</div>}
      {status === 'error' && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">Failed to send message. {errorMsg ? `Reason: ${errorMsg}` : ''}</div>}

      <button type="submit" disabled={isSubmitting} className="w-full bg-[#007A3D] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#C6A44C] transition-colors disabled:opacity-50">
        {isSubmitting ? 'Sending...' : formType === 'admission' ? 'Submit Application' : 'Send Message'}
      </button>
    </form>
  );
};

export default SharedForm;
